import { useCallback, useEffect, useRef, useState } from "react";
import "./Toast.css";

export type ToastTone = "info" | "warn";
export type ToastState = { seq: number; message: string; tone: ToastTone } | null;

/** Page-local transient toast (the SPA has no global toast provider). `useToast`
 *  owns the message + auto-dismiss; render a single `<Toast toast={toast} />`
 *  near the page root. The entry pops up + fades in from the bottom; the exit
 *  fades out in place. `seq` bumps per message so back-to-back toasts remount and
 *  replay the entry animation. `tone` defaults to "info" (dark snackbar); "warn"
 *  uses the warning palette. */
export function useToast(durationMs = 2800) {
  const [toast, setToast] = useState<ToastState>(null);
  const seq = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = useCallback((message: string, tone: ToastTone = "info") => {
    setToast({ seq: (seq.current += 1), message, tone });
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(null), durationMs);
  }, [durationMs]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  return { toast, showToast };
}

// Keep in sync with the toast-out keyframe duration in Toast.css.
const EXIT_MS = 200;

export function Toast({ toast }: { toast: ToastState }) {
  // Hold the last message through the exit animation: when `toast` clears we keep
  // rendering it with the leaving class until the fade-out finishes, then unmount.
  const [shown, setShown] = useState<ToastState>(toast);
  const [leaving, setLeaving] = useState(false);
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (exitTimer.current) clearTimeout(exitTimer.current);
    if (toast) {
      setLeaving(false);
      setShown(toast);
    } else if (shown) {
      setLeaving(true);
      exitTimer.current = setTimeout(() => {
        setShown(null);
        setLeaving(false);
      }, EXIT_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast]);

  useEffect(() => () => { if (exitTimer.current) clearTimeout(exitTimer.current); }, []);

  if (!shown) return null;
  return (
    <div
      key={shown.seq}
      className={`toast toast--${shown.tone}${leaving ? " toast--leaving" : ""}`}
      role="status"
    >
      {shown.message}
    </div>
  );
}
